import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BlockReview, PestCell } from '../../../scouting/reviewModel';
import type { SpecMeasurementType } from '../../../scouting/pestConfig';
import { ReviewMeter } from './ReviewMeter';
import PhotoViewModal from '../../PhotoViewModal';
import type { PhotoData } from '../../../types/scouting';
import { formatDate } from '../../../dateFormat';

const GROUPS: { type: SpecMeasurementType; labelKey: string }[] = [
  { type: 'incidence', labelKey: 'fieldScout.review.group.incidence' },
  { type: 'level', labelKey: 'fieldScout.review.group.level' },
  { type: 'count', labelKey: 'fieldScout.review.group.count' },
];

const Row: React.FC<{ cell: PestCell }> = ({ cell }) => {
  const { t } = useTranslation();
  if (!cell.flag) return null; // out-of-protocol → not shown in detail
  return (
    <div className="rev-prow" data-testid={`review-row-${cell.code}`}>
      <span className="rev-prow__code">{cell.code}</span>
      <div className="rev-prow__mid">
        <div className="rev-prow__vs">{t('fieldScout.review.vs', { threshold: cell.threshold })}</div>
        <ReviewMeter value={cell.value} threshold={cell.threshold} flag={cell.flag} />
      </div>
      <span className={`rev-prow__val rev-prow__val--${cell.flag}`}>{cell.display}</span>
      <span className={`rev-pill rev-pill--${cell.flag}`}>{t(`fieldScout.review.${cell.flag}`)}</span>
    </div>
  );
};

export const BlockDetailPanel: React.FC<{ block: BlockReview; onGenerateReport: (block: BlockReview) => void }> = ({ block, onGenerateReport }) => {
  const { t } = useTranslation();
  const inProtocol = block.cells.filter(c => c.inProtocol && c.flag);
  const [showPhotos, setShowPhotos] = useState(false);
  const photoEntries: PhotoData[] = (block.photos || []).map(p => ({
    photoUrl: p.photoUrl,
    treeIndex: 0,
    pointIndex: p.inspectionPointId,
    timestamp: block.date,
    pestId: '',
  }));
  return (
    <div className="card card-pad rev-detail" data-testid="block-detail-panel">
      <div className="rev-detail__head">
        <span className="rev-detail__title">{block.orchardName} · {block.variety}</span>
        {block.counts.exceeded > 0 && (
          <span className="rev-pill rev-pill--exceeded">{t('fieldScout.review.summary.kOver', { count: block.counts.exceeded })}</span>
        )}
      </div>
      <div className="rev-detail__meta">
        {t('fieldScout.review.summary.pointsMeta', { examined: block.examinedCount, total: block.totalInspectionPoints, scouter: block.scouterName, date: formatDate(block.date) })}
      </div>
      <div className="rev-sumrow">
        <div className="rev-sumrow__s rev-sumrow__s--exceeded"><div className="n">{block.counts.exceeded}</div><div className="l">{t('fieldScout.review.exceeded')}</div></div>
        <div className="rev-sumrow__s rev-sumrow__s--approaching"><div className="n">{block.counts.approaching}</div><div className="l">{t('fieldScout.review.approaching')}</div></div>
        <div className="rev-sumrow__s rev-sumrow__s--below"><div className="n">{block.counts.below}</div><div className="l">{t('fieldScout.review.below')}</div></div>
      </div>
      {GROUPS.map(g => {
        const rows = inProtocol.filter(c => c.type === g.type);
        if (!rows.length) return null;
        return (
          <div key={g.type} className="rev-group">
            <div className="section-label">{t(g.labelKey)}</div>
            {rows.map(c => <Row key={c.code} cell={c} />)}
          </div>
        );
      })}
      {photoEntries.length > 0 && (
        <button type="button" className="btn btn-outline-secondary w-100 mt-3" data-testid="detail-view-photos"
          onClick={() => setShowPhotos(true)}>
          {t('fieldScout.review.viewPhotos', { count: photoEntries.length })}
        </button>
      )}
      <PhotoViewModal show={showPhotos} onHide={() => setShowPhotos(false)} photos={photoEntries} scouterName={block.scouterName} />
      <button type="button" className="btn btn-warning w-100 mt-3" data-testid="detail-generate-report"
        onClick={() => onGenerateReport(block)}>
        {t('fieldScout.review.summary.generateReport', { block: block.blockId })}
      </button>
    </div>
  );
};

export default BlockDetailPanel;
